export * from "./util-strings-email"
export * from "./util-strings-file"
export * from "./util-strings-user"
export * from "./util-strings-xml"

import { clamp, get } from "nodash"
const { floor } = Math

const defaultOptions = { maxLength: 10, separator: "...", distribution: 0.5 }

/**
 * a barebones trim helper to cut out the middle of very long strings.
 * should be expanded with better options when time permits...
 *
 * @param value
 * @param maxLength
 * @param separator
 * @returns string
 */
export const trimMiddle = (
    value: string,
    optionOverrides: {
        maxLength?: number
        separator?: string
        distribution?: number
    } = {}
): string => {
    const options = {
        ...defaultOptions,
        ...optionOverrides,
    }
    const { maxLength, separator, distribution: unguardedDistribution } = options

    const length = value.length
    if (length <= maxLength) {
        return value
    }

    const distribution = clamp(0, unguardedDistribution, 1)
    const showCount = floor(maxLength - separator.length)
    const showLeftCount = showCount - floor(showCount * distribution)
    const showRightCount = showCount - floor(showCount * (1 - distribution))
    const fromExpression = `^(.{0,${showLeftCount}}).*(.{${showRightCount}})`
    const expression = RegExp(fromExpression)
    const result = value.match(expression)

    if ((result?.length || 0) < 3) {
        return value
    }

    const front = get(result, 1)
    const back = get(result, 2)
    return `${front}${separator}${back}`
}

/**
 * takes in a unknown string type and normalizes it so that it returns an expected type of string
 * this is useful for URL parameters where they could be any type but you want them to be a string
 *
 * @returns string
 */
export function normalize(q: string | string[] | undefined) {
    if (typeof q === "string") {
        return q
    } else if (Array.isArray(q)) {
        if (q.length === 0) {
            return ""
        }
        return q[0]
    }
    return ""
}

/**
 * Adds an 's to the end of a string, accounting for cases where the string already ends in S and needs just an apostrophe.
 *
 * @param value the string to add an apostrophe to
 */
export function appendApostropheToString(value: string) {
    if (value === "") {
        return value
    }
    const lastCharacter = value.charAt(value.length - 1)
    if (lastCharacter.toLocaleLowerCase() === "s") {
        return value + "'"
    }
    return value + "'s"
}

export function findEnumKeyByValue(
    enumObject: Record<string, string | number>,
    targetValue: string | number
) {
    for (const key in enumObject) {
        if (enumObject[key] === targetValue) {
            return key
        }
    }
    return null
}

/**
 * Take a string and a break length and split it into two parts at that index
 * @param name The string you want to break into two
 * @param breakLength The index you want to break at
 * @returns {first: string, second?: string} If your string is less than the break length, only the first half will return.
 */
export function splitLongString(
    name: string,
    breakLength: number
): { first: string; second?: string } {
    if (name.length >= breakLength) {
        const firstHalf = name.slice(0, breakLength)
        const secondHalf = name.slice(breakLength)
        return { first: firstHalf, second: secondHalf }
    }

    return { first: name }
}

/**
 *
 * Used in OrganizationEditorModal for formatting the notifcation error message that appears when fields are missing
 * The parameters are the possible fields, and you pass in whether they are defined or not
 * The returned string should be provided to the Global Notification
 */
export const formatErrorMessage = (name?: boolean, region?: boolean) => {
    const first = "Please fill out the required information:\n"
    const fields = []
    if (!name) fields.push("Workspace Name")
    if (!region) fields.push("Data Residency")

    let second = ""
    if (fields.length === 2) {
        second = `${fields[0]} and ${fields[1]}`
    }
    if (fields.length === 1) {
        second = fields[0]
    }
    return first + second
}

//DeckGL layers need colors to be RGB, but we commonly store colors in Hex
//This function takes a hex string and converts it into an RGB Array that DeckGL can use
//Returns an RGBA array if the hexcode specified an alpha value
export const convertHexStringToRGBArray = (
    hex?: string
): [number, number, number] | [number, number, number, number] => {
    if (!hex) return [0, 0, 0]
    hex = hex.replace(/^#/, "")

    // Check if the hex code includes alpha
    let alpha = 255
    if (hex.length === 8) {
        alpha = parseInt(hex.slice(6, 8), 16)
        hex = hex.slice(0, 6)
    }

    // Parse hexadecimal value to RGB
    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255

    // Check for valid hexadecimal color
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return [0, 0, 0]
    }

    if (alpha !== 255) return [r, g, b, alpha]
    return [r, g, b]
}

export const convertRGBAArrayToHexString = (rgba: [number, number, number, number]) => {
    return "#" + rgba.map((e) => e.toString(16).padStart(2, "0")).join("")
}

export const convertRGBAArrayToKMLColour = (rgba: [number, number, number, number]) =>
    rgba
        .reverse()
        .map((num) => num.toString(16))
        .join("")
export const convertColourToKMLColour = (colour: string | undefined) => {
    if (!colour) return
    // KML requires 8-char AABBGGRR. If input is 6-char RGB (no alpha), pad with FF (fully opaque).
    const hex = colour.startsWith("#") ? colour.slice(1) : colour
    const padded = hex.length === 6 ? hex + "FF" : hex
    let reversed = ""
    for (let i = 0; i < padded.length; i += 2) {
        reversed = padded.substring(i, i + 2) + reversed
    }
    return reversed
}

export const formatNumberWithCommas = (num: number | string) => {
    if (typeof num === "string") num = Number(num)
    if (num < 1000) {
        if (num !== Math.floor(num)) return num.toFixed(2)
        return num
    }
    const chars = num.toFixed(0).split("")
    return chars.reduce((acc, cur, i) => {
        if ((chars.length - i) % 3 === 0 && i !== 0 && i !== chars.length - 1) acc += ","
        acc += cur
        return acc
    }, "")
}

/**
 * Performs a natural sort on two strings.
 * This function compares numeric parts of strings as numbers
 * and non-numeric parts lexicographically.
 *
 * Solves "A-5" coming between "A-49" and "A-50"
 *
 * @param {string} a - The first string to compare.
 * @param {string} b - The second string to compare.
 * @returns {number} - A negative value if `a` should come before `b`,
 *                     a positive value if `a` should come after `b`,
 *                     or 0 if they are considered equal.
 * @example
 *
 * const a = "hello"
 * const b = "world"
 * .sort((a, b) => naturalSort(a, b))
 *
 */
export const naturalSort = (a: string, b: string): number => {
    const re = /(\d+)|(\D+)/g

    // Match the sequences of digits or non-digits in the strings
    const aParts = a.match(re) || []
    const bParts = b.match(re) || []

    // While there are parts to compare in both strings
    while (aParts.length && bParts.length) {
        const aPart = aParts.shift()
        const bPart = bParts.shift()

        // If either part is undefined, fallback to localeCompare
        if (aPart === undefined || bPart === undefined) {
            return a.localeCompare(b)
        }

        const aNum = parseInt(aPart, 10)
        const bNum = parseInt(bPart, 10)

        // If either part is not a number, compare them as strings
        if (isNaN(aNum) || isNaN(bNum)) {
            if (aPart !== bPart) {
                return aPart.localeCompare(bPart)
            }
        } else {
            // If both parts are numbers, compare them numerically
            if (aNum !== bNum) {
                return aNum - bNum
            }
        }
    }

    // If one of the arrays still has parts left, the longer one should come later
    return aParts.length - bParts.length
}

export function reduceHexStringOpacity(hex: string, percentage: number): string {
    // Ensure the hex string is in the correct format
    if (hex.startsWith("#")) {
        hex = hex.slice(1)
    }

    // If the hex string is in the format #RRGGBB, add full opacity (FF) to it
    if (hex.length === 6) {
        hex += "FF"
    }

    // Parse the RGBA values from the hex string
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    let a = parseInt(hex.slice(6, 8), 16)

    // Calculate the new alpha value
    a = Math.round(a * (1 - percentage / 100))

    // Convert the RGBA values back to a hex string
    const newHex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
        .toString(16)
        .padStart(2, "0")}${a.toString(16).padStart(2, "0")}`

    return newHex
}

export function opacityDecimalToHex(opacity?: number): string {
    if (opacity === undefined) return "ff"

    const validOpacity = Math.min(1, Math.max(0, opacity))

    const alphaVal = Math.round(validOpacity * 255)
    const hex = alphaVal.toString(16).padStart(2, "0")

    return hex.toUpperCase()
}

export function formatSubscriptionId(subscriptionId: string): string {
    let raw = subscriptionId
    if (raw.startsWith("sub_")) {
        raw = raw.substring(4)
    }

    const part1 = raw.slice(0, 6) // first 6 chars
    const part2 = raw.slice(6, 12) // next 6 chars

    return `${part1}-${part2}`
}

export function formatPrice(price: number): string {
    return `$${Math.round(price).toLocaleString()}`
}

export function getElapsedTimeString(startTime: Date): string {
    const now = new Date()
    const elapsedMs = now.getTime() - startTime.getTime()

    const totalSeconds = Math.floor(elapsedMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const pad = (num: number) => num.toString().padStart(2, "0")

    if (hours > 0) {
        return `${hours}:${pad(minutes)}:${pad(seconds)}`
    } else {
        return `${pad(minutes)}:${pad(seconds)}`
    }
}

export function stringToHex(str: string): string {
    return Array.from(str)
        .map((char) => char.charCodeAt(0).toString(16))
        .join("")
}

export function hexToString(hex: string): string {
    return hex
        .match(/.{1,2}/g)!
        .map((byte) => String.fromCharCode(parseInt(byte, 16)))
        .join("")
}

export const sanitizedPhoneNumber = (number: string) => {
    const digits = number.replace(/\D/g, "")
    if (!/^(?:\d{10}|1\d{10})$/.test(digits)) {
        return null
    }
    const normalized = digits.length === 10 ? `1${digits}` : digits
    return `+${normalized}`
}

export const formatPhoneNumber = (input?: string) => {
    if (!input) return null
    const auMatch = input.match(/^(?:\+?61|0)[\s.-]?(4\d{2}|5\d{2})[\s.-]?(\d{3})[\s.-]?(\d{3})$/)
    if (auMatch) {
        const [, mobilePrefix, first, second] = auMatch
        return `+61 ${mobilePrefix} ${first} ${second}`
    }
    const naMatch = input.match(
        /^(?:\+?1[\s.-]?)?\(?([2-9]\d{2})\)?[\s.-]?([2-9]\d{2})[\s.-]?(\d{4})$/
    )
    if (naMatch) {
        const [, area, first, second] = naMatch
        return `+1 (${area}) ${first}-${second}`
    }
    return null
}

export function getEnumValue<T extends Record<string, string | number>>(
    enumObj: T,
    input: string
): T[keyof T] | undefined {
    const upperInput = input.toUpperCase()

    const key = Object.keys(enumObj).find((k) => k.toUpperCase() === upperInput) as
        | keyof T
        | undefined

    return key !== undefined ? enumObj[key] : undefined
}

export const formatPartialPhoneNumberInput = (originalValue: string): string => {
    if (shouldSkipFormatting(originalValue)) {
        return originalValue
    }

    const digitsOnly = originalValue.replace(/\D/g, "")
    const isPlusPrefixed = originalValue.startsWith("+")

    if (isPlusPrefixed && digitsOnly.startsWith("61")) {
        return formatAU(digitsOnly)
    }

    if (digitsOnly.startsWith("0")) {
        return formatDomestic(digitsOnly)
    }

    if (digitsOnly.startsWith("1")) {
        return formatUS(digitsOnly, isPlusPrefixed)
    }

    return formatDefault(digitsOnly)
}
const shouldSkipFormatting = (val: string): boolean => {
    const isTooShort = val.length <= 4
    const hasInvalidChars = !/^[\d()\-\s+]*$/.test(val)
    return isTooShort || hasInvalidChars
}
const formatAU = (digits: string): string => {
    const rest = digits.slice(2)
    if (rest.length === 0) return "+61"
    if (rest.length <= 3) return `+61 ${rest}`
    if (rest.length <= 6) return `+61 ${rest.slice(0, 3)} ${rest.slice(3)}`
    return `+61 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6, 9)}`
}
const formatDomestic = (digits: string): string => {
    if (digits.length <= 7) {
        return `${digits.slice(0, 4)} ${digits.slice(4)}`
    }
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`
}
const formatUS = (digits: string, usePlus: boolean): string => {
    const prefix = usePlus ? "+1" : "1"
    const rest = digits.slice(1)

    if (rest.length === 0) return prefix
    if (rest.length <= 3) return `${prefix} (${rest})`
    if (rest.length <= 6) return `${prefix} (${rest.slice(0, 3)}) ${rest.slice(3)}`
    return `${prefix} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6, 10)}`
}
const formatDefault = (digits: string): string => {
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

export const sanitizeMissionName = (name: string): string => {
    return name
        .trim()
        .replace(/[^a-zA-Z0-9-]/g, "-") // Replace invalid chars with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single
        .replace(/^-|-$/g, "") // Remove leading/trailing hyphens
}

export const pathTimestamp = () => {
    const now = new Date()
    const pad = (value: number, length = 2) => String(value).padStart(length, "0")
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
    const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`
    return `${date}_${time}`
}

type PathTemplateContext = {
    org: string
    project: string
    timestamp: string
    tags: string[]
    groups: string[]
}

const joinNames = (names: string[]) => names.map(sanitizeMissionName).join("/")

const nameAt = (names: string[], position: string) =>
    sanitizeMissionName(names[Number(position) - 1] ?? "")

const resolvePathVariable = (name: string, context: PathTemplateContext) => {
    if (name === "org") return sanitizeMissionName(context.org)
    if (name === "project") return sanitizeMissionName(context.project)
    if (name === "timestamp") return context.timestamp
    if (name === "...tags") return joinNames(context.tags)
    if (name === "...groups") return joinNames(context.groups)
    if (name.startsWith("tag")) return nameAt(context.tags, name.slice(3))
    if (name.startsWith("group")) return nameAt(context.groups, name.slice(5))
    return ""
}

export const resolvePathTemplate = (template: string, context: PathTemplateContext) => {
    const [start, ...variables] = template.split("{")
    return variables
        .reduce((path, variable) => {
            const [name, ...literal] = variable.split("}")
            return path + resolvePathVariable(name!, context) + literal.join("}")
        }, start!)
        .split("/")
        .filter(Boolean)
        .join("/")
}

export function pluralize({
    count,
    includeCount = true,
    str,
    single,
    plural,
}: { count: number; includeCount?: boolean } & (
    | { str: string; single?: never; plural?: never }
    | { str?: never; single: string; plural: string }
)) {
    let out = includeCount ? `${count} ` : ""
    if (count === 1) {
        out += single ?? str
    } else {
        out += plural ?? `${str}s`
    }
    return out
}

export function manuallyTruncateLongString(str: string, maxChars: number) {
    if (str.length <= maxChars) return str
    return `${str.slice(0, maxChars)}...`
}

export function isFilenameForPdf(filename: string | null | undefined) {
    return !!filename && /\.pdf$/i.test(filename)
}
export function isFilenameForImage(filename: string | null | undefined) {
    return !!filename && /\.(png|jpg|jpeg)$/i.test(filename)
}

export function getDateTimeString(date: Date | null | undefined) {
    if (!date) return
    return `${date.toDateString()} @ ${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`
}
